import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './About.css';

const About = () => {
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6
      }
    }
  };

  const stats = [
    { number: '4+', label: 'Years of Study' },
    { number: '10+', label: 'Projects Completed' },
    { number: '5+', label: 'Technologies' },
    { number: '100%', label: 'Dedication' }
  ];

  return (
    <section id="about" className="about" ref={ref}>
      <div className="container">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.h2 className="section-title" variants={itemVariants}>
            About Me
          </motion.h2>

          <div className="about-content">
            <motion.div className="about-text" variants={itemVariants}>
              <motion.p variants={itemVariants}>
                I'm a passionate <strong>BS Cybersecurity Graduate</strong> from FAST-NUCES with a deep 
                interest in ethical hacking, vulnerability assessment, Privileged Access Management and digital defense. My journey 
                in cybersecurity began with curiosity about how systems work and how to protect them.
              </motion.p>

              <motion.p variants={itemVariants}>
                Through my academic experience and hands-on projects, I've developed solid skills in 
                <strong> C++</strong> and <strong>Python</strong>, along with practical experience using 
                tools like <strong>Wireshark, Nmap, Burp Suite, Kron PAM and Metasploit</strong>. I enjoy solving 
                real-world security challenges through code and creative thinking.
              </motion.p>

              <motion.p variants={itemVariants}>
                My expertise spans both <strong>offensive and defensive security</strong>, with experience 
                in vulnerability analysis, incident response, Access Management and threat detection. I'm also exploring 
                <strong> AI in cybersecurity</strong> and have built web development skills to better 
                understand web-based vulnerabilities.
              </motion.p>

              <motion.div className="education-highlight" variants={itemVariants}>
                <h3>Education</h3>
                <div className="education-item">
                  <h4>National University of Computer and Emerging Sciences (FAST-NUCES)</h4>
                  <p>BS Cybersecurity • 2022 - 2026</p>
                  <p>Gained practical experience in vulnerability analysis, ethical hacking, and incident response</p>
                </div>
                <div className="education-item">
                  <h4>Punjab College of Information Technology (PCIT)</h4>
                  <p>Pre Engineering • 2020 - 2021 • Grade: A+</p>
                  <p>Strong foundation in Physics, Chemistry, and Mathematics</p>
                </div>
              </motion.div>
            </motion.div>

            <motion.div className="about-stats" variants={itemVariants}>
              <div className="stats-grid">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    className="stat-item"
                    variants={itemVariants}
                    whileHover={{ scale: 1.05 }}
                  >
                    <motion.div 
                      className="stat-number"
                      initial={{ scale: 0 }}
                      animate={inView ? { scale: 1 } : { scale: 0 }}
                      transition={{ delay: 0.5 + index * 0.1, type: "spring" }}
                    >
                      {stat.number}
                    </motion.div>
                    <div className="stat-label">{stat.label}</div>
                  </motion.div>
                ))}
              </div>

              <motion.div className="skills-preview" variants={itemVariants}>
                <h3>Core Competencies</h3>
                <div className="skills-list">
                  {[
                    'Vulnerability Assessment',
                    'Penetration Testing', 
                    'Incident Response',
                    'Ethical Hacking',
                    'Security Auditing',
                    'Network Security',
                    'PAM - Privileged Access Management (Krontech)'
                  ].map((skill, index) => (
                    <motion.span
                      key={skill}
                      className="skill-tag"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }}
                      transition={{ delay: 0.7 + index * 0.1 }}
                      whileHover={{ scale: 1.1 }}
                    >
                      {skill}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;